import { Auth0Error } from 'auth0-js';
import { useContext, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AuthHeader from './AuthHeader';
import { AnalyticsContext } from 'src/AnalyticsContext';
import { useSageAuth } from 'src/auth/useSageAuth';
import StyledOtpInput from 'src/component-library/OtpInput';
import DetailsPageHeader from 'src/components/fragments/DetailsPageHeader';

const DefaultCountdown = 30;
const PageName = 'Code Verification Page';

interface ConfirmLoginCodeProps {
    phoneNumber: string;
    onBack: () => void;
}

export default function ConfirmLoginCode(props: ConfirmLoginCodeProps) {
    const { phoneNumber, onBack } = props;
    const { trackEvent, trackPageView } = useContext(AnalyticsContext);
    const { zIndex } = useTheme();

    const { sendOtp, verifyOtp } = useSageAuth();
    const [otp, setOtp] = useState('');
    const [seconds, setSeconds] = useState(DefaultCountdown);
    const [isVerifyingCode, setIsVerifyingCode] = useState(false);
    const [requiresNewCode, setRequiresNewCode] = useState(false);
    const [authError, setAuthError] = useState<Auth0Error>();

    const handleOnOtpChange = (newOtp: string) => {
        if (requiresNewCode) {
            setRequiresNewCode(false);
        }

        setOtp(newOtp);
    };

    const isSubmitButtonDisabled = otp?.length < 6 || requiresNewCode || isVerifyingCode;
    const showErrorStyles = !!(authError && requiresNewCode);

    // countdown effect
    useEffect(() => {
        const countdownTimer = setInterval(() => {
            if (seconds > 0) {
                setSeconds(seconds - 1);
            }
        }, 1000);
        return () => {
            clearInterval(countdownTimer);
        };
    }, [seconds]);

    useEffect(() => {
        // make sure we are scrolled to the top with mobile Safari's collapsing URL bar
        window.scrollTo(0, 0);

        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResendCode = () => {
        setAuthError(undefined);
        setOtp('');
        setSeconds(DefaultCountdown);

        trackEvent('Resend Code Clicked');
        sendOtp(phoneNumber)
            .then(() => {
                setOtp('');
                setSeconds(DefaultCountdown);
            })
            .catch((error) => {
                setAuthError(error);
                datadogRum.addError(error, { message: 'Error resending code' });
            });
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        trackEvent('Continue Button Clicked', { context: PageName });
        setIsVerifyingCode(true);

        verifyOtp(phoneNumber, otp)
            .then(() => {
                // The web flow redirects to /auth_confirmed as part of verifying.
                setRequiresNewCode(false);
                setAuthError(undefined);
            })
            .catch((err) => {
                setAuthError(err);
                setRequiresNewCode(true);
                setOtp('');
                window.scrollTo({ top: 0 });
            })
            .finally(() => {
                setIsVerifyingCode(false);
            });
    };

    return (
        <>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: zIndex.modal,
                }}
            >
                <DetailsPageHeader
                    analyticsPrefix={PageName}
                    pageTitle="Verify your identity"
                    onBack={onBack}
                />
                <AuthHeader state={authError?.code} />
            </Box>
            <Box sx={{ position: 'relative', p: 2.5 }}>
                <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Enter Verification Code
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                    Enter the 6 digit verification code sent to
                    <br />
                    (•••) ••• &nbsp;
                    {phoneNumber.slice(8, 12)}
                </Typography>

                <Stack sx={{ gap: 2 }}>
                    <Typography variant="body2">
                        Not your number? Tap the back arrow above to provide a different one.
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <StyledOtpInput
                            value={otp}
                            onChange={handleOnOtpChange}
                            hasErrored={showErrorStyles}
                        />
                        <Box sx={{ mt: 3, fontWeight: '400' }}>
                            <Button
                                disableElevation
                                disabled={isSubmitButtonDisabled}
                                fullWidth
                                loading={isVerifyingCode}
                                type="submit"
                                variant="contained"
                            >
                                Continue
                            </Button>
                        </Box>
                    </form>
                </Stack>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="body1">Didn&apos;t receive a code?&nbsp;</Typography>
                    {seconds === 0 ? (
                        <Typography
                            variant="body1"
                            onClick={handleResendCode}
                            sx={{ fontWeight: 'bold', color: 'primary.main' }}
                        >
                            Resend the code
                        </Typography>
                    ) : (
                        <Typography>
                            You can resend it in{' '}
                            <strong>00:{seconds < 10 ? `0${seconds}` : seconds}</strong>
                        </Typography>
                    )}
                </Box>
            </Box>
        </>
    );
}
